import { useCallback, useState } from "react";
import type { CreateArgsSF } from "@yuno-payments/sdk-web-types";
import { useTranslation } from "@shared/utils/internationalization";
import Box from "@ui-kit/layout/Box/Box.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { OnChangeArgs } from "@shared/lib/yuno/types";

// eslint-disable-next-line import/no-cycle
import YunoElement from "../YunoElement/YunoElement.web";

function YunoCardExpiryElement({
  onValidField,
  onInvalidField,
}: {
  /**
   * Fires when the field has no errors (field is valid).
   */
  onValidField?: () => void;
  /**
   * Fires when the field has errors (field is invalid).
   */
  onInvalidField?: () => void;
}) {
  const { t } = useTranslation("checkout");
  const [error, setError] = useState("");
  const [touched, setTouched] = useState(false);

  const handleChange: CreateArgsSF["options"]["onChange"] = useCallback(
    (args: OnChangeArgs) => {
      if (args.error) {
        setError(t("Your card's expiration date is invalid."));
        onInvalidField?.();
      } else {
        setError("");
        onValidField?.();
      }
    },
    [onValidField, onInvalidField, t]
  );

  return (
    <Box>
      <Box>
        <Text fontWeight={700} mb="2xs" fontSize="sm">
          {t("Expiration")}
        </Text>
      </Box>
      <Box
        border="1px solid"
        borderColor={error && touched ? "red.600" : "border"}
        borderRadius="sm"
        p="sm"
        bg="bg"
      >
        <YunoElement
          element="expiration"
          onChange={handleChange}
          elementProps={{
            name: "expiration",
            options: {
              placeholder: t("MM / YY"),
              onBlur: () => setTouched(true),
            },
          }}
        />
      </Box>
      <Box>
        <Text
          color="red.600"
          noOfLines={2}
          data-testid="yuno-element-expiration-error"
        >
          {touched ? error : ""}
        </Text>
      </Box>
    </Box>
  );
}

export default YunoCardExpiryElement;
